// src/components/PlacesSearch.js
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { TextField, InputAdornment, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import usePlacesAutocomplete from '../hooks/usePlacesAutocomplete';
import { getPlaceDetails } from '../services/placesService';

export default function PlacesSearch({ value, onChange, onSelect }) {
    const inputRef = useRef(null);
    const autocomplete = usePlacesAutocomplete(inputRef, {
        fields: [
            'place_id',
            'name',
            'formatted_address',
            'geometry',
            'address_components',
            'photos',
            'rating',
            'opening_hours',
        ],
    });

    // when user picks from dropdown
    useEffect(() => {
        if (!autocomplete) return;
        const listener = autocomplete.addListener('place_changed', async () => {
            const acPlace = autocomplete.getPlace();
            if (acPlace.geometry?.location) {
                try {
                    const full = await getPlaceDetails(acPlace.place_id, [
                        'place_id',
                        'name',
                        'formatted_address',
                        'geometry',
                        'address_components',
                        'photos',
                        'rating',
                        'opening_hours',
                    ]);
                    onSelect(full);
                } catch (err) {
                    console.error('Failed to load place details', err);
                }
            }
        });
        return () => listener.remove();
    }, [autocomplete, onSelect]);

    return (
        <TextField
            inputRef={inputRef}
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder="Search for any place…"
            size="small"
            variant="outlined"
            fullWidth
            sx={{
                borderRadius: "50px",
                bgcolor: 'white',
                "& .MuiOutlinedInput-root": {
                    borderRadius: "50px",
                    "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: (theme) => theme.colors.blue,
                    },
                },
                "& .MuiOutlinedInput-input": {
                    padding: "12px 20px 12px 20px !important",
                },
            }}


            InputProps={{
                endAdornment: value && (
                    <InputAdornment position="end">
                        <IconButton size="small" onClick={() => onChange('')}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </InputAdornment>
                ),
            }}
        />
    );
}

PlacesSearch.propTypes = {
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    onSelect: PropTypes.func.isRequired,
};
